// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import { applyPenders } from 'lib/common';
import * as OrdersAPI from 'lib/api/me/orders';

/* ACTION TYPE */
const LIST_ORDER = 'orders/LIST_ORDER';
const DOWNLOAD_INVENTORIES_EXCEL = 'orders/DOWNLOAD_INVENTORIES_EXCEL';
const DOWNLOAD_REQUEST_EXCEL = 'orders/DOWNLOAD_REQUEST_EXCEL';
const GET_ORDER = 'orders/GET_ORDER';
const AUTHENTICATE = 'orders/AUTHENTICATE';
const CANCEL_ORDER = 'orders/CANCEL_ORDER';
const DELETE_ORDER = 'orders/DELETE_ORDER';
const UPDATE_TRANSACTIONS = 'orders/UPDATE_TRANSACTIONS';
const MARK_AS_NOT_RECEIVED = 'orders/MARK_AS_NOT_RECEIVED';
const MARK_AS_RECEIVED = 'orders/MARK_AS_RECEIVED';
const UPDATE_ADD_REFUND = 'orders/UPDATE_ADD_REFUND';
const UPDATE_REMOVE_REFUND = 'orders/UPDATE_REMOVE_REFUND';
const UPDATE_REFUND_QUANTITY = 'orders/UPDATE_REFUND_QUANTITY';
const REQUEST_REFUND = 'orders/REQUEST_REFUND';
const CANCEL_REFUND = 'orders/CANCEL_REFUND';
const UPDATE_REFUND_CANCELLATION = 'orders/UPDATE_REFUND_CANCELLATION';
const UPDATE_REFUND_LIST = 'orders/UPDATE_REFUND_LIST';

/* ACTION CREATOR */
const listOrder = createAction(LIST_ORDER, OrdersAPI.listForMe);
const downloadInventoriesExcel = createAction(
  DOWNLOAD_INVENTORIES_EXCEL,
  OrdersAPI.downloadInventoriesExcelForMe,
);
const downloadRequestsExcel = createAction(
  DOWNLOAD_REQUEST_EXCEL,
  OrdersAPI.downloadRequestsExcelForMe,
);
const getOrder = createAction(GET_ORDER, OrdersAPI.getForMe);
const authenticate = createAction(AUTHENTICATE, OrdersAPI.authenticate);
const cancelOrder = createAction(CANCEL_ORDER, OrdersAPI.cancelForMe);
const deleteOrder = createAction(DELETE_ORDER, OrdersAPI.deleteForMe);
const updateTransactions = createAction(UPDATE_TRANSACTIONS, OrdersAPI.updateTransactionsForMe);
const markAsNotReceived = createAction(MARK_AS_NOT_RECEIVED, OrdersAPI.markAsNotReceivedForMe);
const markAsReceived = createAction(MARK_AS_RECEIVED, OrdersAPI.markAsReceivedForMe);
const updateAddRefund = createAction(UPDATE_ADD_REFUND);
const updateRemoveRefund = createAction(UPDATE_REMOVE_REFUND);
const updateRefundQuantity = createAction(UPDATE_REFUND_QUANTITY);
const requestRefund = createAction(REQUEST_REFUND, OrdersAPI.requestRefundForMe);
const cancelRefund = createAction(CANCEL_REFUND, OrdersAPI.cancelRefundForMe);
const updateRefundCancellation = createAction(
  UPDATE_REFUND_CANCELLATION,
  OrdersAPI.updateRefundCancellationForMe,
);
const updateRefundList = createAction(UPDATE_REFUND_LIST);

/* ACTION FLOW TYPE */
type ListOrderAction = ActionType<typeof listOrder>;
type DownloadInventoriesExcelAction = ActionType<typeof downloadInventoriesExcel>;
type DownloadRequestsExcelAction = ActionType<typeof downloadRequestsExcel>;
type GetOrderAction = ActionType<typeof getOrder>;
type AuthenticateAction = ActionType<typeof authenticate>;
type CancelOrderAction = ActionType<typeof cancelOrder>;
type DeleteOrderAction = ActionType<typeof deleteOrder>;
type UpdateTransactionsAction = ActionType<typeof updateTransactions>;
type MarkAsNotReceivedAction = ActionType<typeof markAsNotReceived>;
type MarkAsReceivedAction = ActionType<typeof markAsReceived>;
type UpdateAddRefundAction = ActionType<typeof updateAddRefund>;
type UpdateRemoveRefundAction = ActionType<typeof updateRemoveRefund>;
type UpdateRefundQuantityAction = ActionType<typeof updateRemoveRefund>;
type RequestRefundAction = ActionType<typeof requestRefund>;
type CancelRefundAction = ActionType<typeof cancelRefund>;
type UpdateRefundCancellationAction = ActionType<typeof updateRefundCancellation>;
type UpdateRefundListAction = ActionType<typeof updateRefundList>;

/* ACTION CREATORS INTERFACE */
export interface OrdersActionCreators {
  listOrder(payload: ListOrderAction): any;
  downloadInventoriesExcel(payload: DownloadInventoriesExcelAction): any;
  downloadRequestsExcel(payload: DownloadRequestsExcelAction): any;
  getOrder(payload: GetOrderAction): any;
  authenticate(payload: AuthenticateAction): any;
  cancelOrder(payload: CancelOrderAction): any;
  deleteOrder(payload: DeleteOrderAction): any;
  updateTransactions(payload: UpdateTransactionsAction): any;
  markAsNotReceived(payload: MarkAsNotReceivedAction): any;
  markAsReceived(payload: MarkAsReceivedAction): any;
  updateAddRefund(payload: UpdateAddRefundAction): any;
  updateRemoveRefund(payload: UpdateRemoveRefundAction): any;
  updateRefundQuantity(payload: UpdateRefundQuantityAction): any;
  requestRefund(payload: RequestRefundAction): any;
  cancelRefund(payload: CancelRefundAction): any;
  updateRefundCancellation(payload: UpdateRefundCancellationAction): any;
  updateRefundList(payload: UpdateRefundListAction): any;
}

/* EXPORT ACTION CREATORS */
export const actionCreators: OrdersActionCreators = {
  listOrder,
  downloadInventoriesExcel,
  downloadRequestsExcel,
  getOrder,
  authenticate,
  cancelOrder,
  deleteOrder,
  updateTransactions,
  markAsNotReceived,
  markAsReceived,
  updateAddRefund,
  updateRemoveRefund,
  updateRefundQuantity,
  requestRefund,
  cancelRefund,
  updateRefundCancellation,
  updateRefundList,
};

/* STATE TYPES */
export type Orders = {
  items: any,
  order: any,
};

/* INITIAL STATE */
const initialState: Orders = {
  items: [],
  order: {},
  transactions: {},
};

/* REDUCER */
const reducer = handleActions(
  {
    [LIST_ORDER]: (state, action: ListOrderAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.items = action.payload;
      });
    },
    [DOWNLOAD_INVENTORIES_EXCEL]: (state, action: DownloadInventoriesExcelAction) => {
      return produce(state, draft => {
        // if (!action) return;
        // draft.items = action.payload;
      });
    },
    [DOWNLOAD_REQUEST_EXCEL]: (state, action: DownloadRequestsExcelAction) => {
      return produce(state, draft => {
        // if (!action) return;
        // draft.items = action.payload;
      });
    },
    [GET_ORDER]: (state, action: GetOrderAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.order = action.payload;
      });
    },
    [UPDATE_ADD_REFUND]: (state, action: UpdateAddRefundAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.order.items[action.payload].refunded = true;
        draft.order.items[action.payload].refundQuantity =
          draft.order.items[action.payload].quantity;
      });
    },
    [UPDATE_REMOVE_REFUND]: (state, action: UpdateRemoveRefundAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.order.items[action.payload].refunded = false;
        draft.order.items[action.payload].refundQuantity = 0;
      });
    },
    [UPDATE_REFUND_QUANTITY]: (state, action: UpdateRefundQuantityAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.order.items[action.payload.index].refundQuantity = Number(action.payload.quantity);
      });
    },
    [UPDATE_REFUND_LIST]: (state, action: UpdateRefundQuantityAction) => {
      return produce(state, draft => {
        if (!action) return;
        const itemList = action.payload;
        // eslint-disable-next-line array-callback-return,consistent-return
        const filtered = draft.order.items.filter(item => {
          if (!itemList.includes(item._id)) return item;
        });
        draft.order.items = filtered;
      });
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: LIST_ORDER,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.items = data.items || data;
      });
    },
    onError: state =>
      produce(state, draft => {
        // draft.items = [];
      }),
  },
  {
    type: DOWNLOAD_INVENTORIES_EXCEL,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        // draft.items = data;
      });
    },
    onError: state =>
      produce(state, draft => {
        // draft.items = [];
      }),
  },
  {
    type: DOWNLOAD_REQUEST_EXCEL,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        // draft.items = data;
      });
    },
    onError: state =>
      produce(state, draft => {
        // draft.items = [];
      }),
  },
  {
    type: GET_ORDER,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.order = data;
      });
    },
    onError: state =>
      produce(state, draft => {
        draft.order = {};
      }),
  },
  {
    type: CANCEL_ORDER,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {});
    },
    onError: state => produce(state, draft => {}),
  },
  {
    type: DELETE_ORDER,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {});
    },
    onError: state => produce(state, draft => {}),
  },
  {
    type: UPDATE_TRANSACTIONS,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.transactions = data;
      });
    },
    onError: state =>
      produce(state, draft => {
        draft.transactions = {};
      }),
  },
  {
    type: MARK_AS_NOT_RECEIVED,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {});
    },
    onError: state => produce(state, draft => {}),
  },
  {
    type: MARK_AS_RECEIVED,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {});
    },
    onError: state => produce(state, draft => {}),
  },
  {
    type: REQUEST_REFUND,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {});
    },
    onError: state => produce(state, draft => {}),
  },
  {
    type: CANCEL_REFUND,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {});
    },
    onError: state => produce(state, draft => {}),
  },
  {
    type: UPDATE_REFUND_CANCELLATION,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {});
    },
    onError: state => produce(state, draft => {}),
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/orders.js